import { i18n } from "@tellescope/i18n";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Dispatch, SetStateAction } from "react";
import { createStore } from "@tellescope/store";

/** URL viewId used by `/settings/team/views/:viewId` for the unfiltered all-members view. */
export const ALL_TEAM_VIEW_ID = "all";

const getDefaultTitle = () => i18n.t("Default View", { ns: "settings" });

export const DEFAULT_TEAM_VIEW = {
  id: "default",
  businessId: "",
  updatedAt: new Date(0),
  creator: "",
  title: getDefaultTitle(),
  page: "TeamMembers",
  columns: [
    { field: "ID" },
    { field: "Email" },
    { field: "FirstName" },
    { field: "LastName" },
    { field: "LastActive" },
    { field: "Tags" },
    { field: "Role" },
  ],
  createdAt: new Date(0),
} as const satisfies TellescopeModelFromName<"table_views">;

interface TeamDefaultViewStore {
  defaultViewByUserId: Record<string, TellescopeModelFromName<"table_views">>;
  setUserDefaultView: (
    userId: string,
    newVal: Parameters<Dispatch<SetStateAction<TellescopeModelFromName<"table_views">>>>[0]
  ) => void;
  /** URL `:viewId` of the last `/settings/team/views/:viewId` the user visited. */
  lastSelectedViewId: string;
  setLastSelectedViewId: (viewId: string) => void;
}

// Intentionally NOT persisted: default-view filter/column changes are treated as an ephemeral draft
// (calendar-style). They drive the live table + the "Save as View" prompt, but reset to the pristine
// default on reload — the user persists them only by saving a named view.
export const { useStore: useTeamDefaultViewStore, useShallowStore: useTeamDefaultViewShallowStore } =
  createStore<TeamDefaultViewStore>((set) => ({
    defaultViewByUserId: {},
    setUserDefaultView: (userId, newVal) =>
      set((state) => {
        const currentValue = state.defaultViewByUserId[userId] ?? DEFAULT_TEAM_VIEW;
        const resolvedVal = typeof newVal === "function" ? newVal(currentValue) : newVal;

        return {
          ...state,
          defaultViewByUserId: {
            ...state.defaultViewByUserId,
            [userId]: resolvedVal,
          },
        };
      }),
    lastSelectedViewId: ALL_TEAM_VIEW_ID,
    setLastSelectedViewId: (viewId) => set({ lastSelectedViewId: viewId }),
  }));
